import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatName } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Calendar, CreditCard, User, Clock, Video, Phone, MapPin, ArrowRight, Plus, ChevronLeft, ChevronRight, X, Pencil, Trash2, Repeat } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SearchableSelect from '@/components/SearchableSelect';

function ClientDashboard() {
  const { user } = useAuth();
  const [quote, setQuote] = useState({ text: '', author: '' });
  const [todaySessions, setTodaySessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [txSummary, setTxSummary] = useState({ total: 0 });
  const [therapist, setTherapist] = useState(null);
  const [branchMap, setBranchMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [qRes, sRes, stRes, txRes, bRes] = await Promise.all([
          api.get('/quotes/daily'),
          api.get('/sessions'),
          api.get('/sessions/stats/summary'),
          api.get('/transactions/summary'),
          api.get('/branches').catch(() => ({ data: [] })),
        ]);
        setQuote(qRes.data);
        setStats(stRes.data);
        setTxSummary(txRes.data);
        const map = {};
        (bRes.data || []).forEach(b => { map[b._id] = b; });
        setBranchMap(map);
        const today = new Date().toISOString().split('T')[0];
        const sessions = sRes.data;
        setTodaySessions(sessions.filter(s => s.date === today && (s.status === 'SCHEDULED' || s.status === 'CHECKED_IN')));
        setPastSessions(sessions.filter(s => s.status === 'COMPLETED').slice(0, 3));
        setUpcomingSessions(sessions.filter(s => s.date > today && s.status === 'SCHEDULED').slice(0, 3));
        if (user?.primary_therapist_id) {
          const tRes = await api.get(`/therapists/${user.primary_therapist_id}`);
          setTherapist(tRes.data);
        }
      } catch {}
    };
    fetchData();
  }, [user]);

  const typeIcon = (type) => {
    if (type === 'VIDEO') return <Video className="w-4 h-4" />;
    if (type === 'TELE') return <Phone className="w-4 h-4" />;
    return <MapPin className="w-4 h-4" />;
  };

  const renderSessionAction = (s) => {
    const canJoin = s.status === 'SCHEDULED' || s.status === 'CHECKED_IN';
    if (!canJoin) return null;
    if (s.session_type === 'VIDEO') {
      if (s.meeting_link) {
        return (
          <a href={s.meeting_link} target="_blank" rel="noreferrer" data-testid={`join-meeting-link-${s._id}`}>
            <Button className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl gap-2" data-testid={`join-meeting-btn-${s._id}`}>
              <Video className="w-4 h-4" /> Join Meeting
            </Button>
          </a>
        );
      }
      return (
        <div className="flex flex-col items-end gap-1" data-testid={`join-meeting-disabled-${s._id}`}>
          <Button disabled className="bg-[#E5F2F1] text-[#4A6B68] rounded-xl gap-2 cursor-not-allowed">
            <Video className="w-4 h-4" /> Join Meeting
          </Button>
          <p className="text-[10px] text-[#D3544E] max-w-[200px] text-right">Meeting link not available yet. Contact your therapist.</p>
        </div>
      );
    }
    if (s.session_type === 'IN_PERSON') {
      const branch = s.branch_id ? branchMap[s.branch_id] : null;
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#F4FAF9] rounded-xl" data-testid={`in-person-info-${s._id}`}>
          <MapPin className="w-4 h-4 text-[#0D8B7D] flex-shrink-0" />
          <div className="text-right">
            <p className="text-xs font-medium text-[#1A3330]">In-Person session at {branch?.name || 'your branch'}</p>
            {branch?.address && <p className="text-[10px] text-[#4A6B68] max-w-[260px] truncate">{branch.address}</p>}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Quote Card */}
      <Card className="bg-gradient-to-r from-[#0D8B7D] to-[#0A6F64] border-0 rounded-2xl overflow-hidden" data-testid="daily-quote">
        <CardContent className="p-6 lg:p-8">
          <p className="text-white/90 text-lg lg:text-xl italic leading-relaxed font-['Work_Sans']">"{quote.text}"</p>
          <p className="text-white/60 text-sm mt-3">— {quote.author}</p>
        </CardContent>
      </Card>

      {/* Today's Session Alert */}
      {todaySessions.length > 0 ? (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl border-l-4 border-l-[#F5B041]" data-testid="today-session-alert">
          <CardContent className="p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A6F64] mb-3">Today's Session</p>
            {todaySessions.map(s => (
              <div key={s._id} className="flex items-center justify-between flex-wrap gap-3" data-testid={`today-session-${s._id}`}>
                <div className="flex items-center gap-3">
                  {typeIcon(s.session_type)}
                  <div>
                    <p className="font-medium text-[#1A3330]">{s.title}</p>
                    <p className="text-sm text-[#4A6B68]">{s.therapist_name} · {s.start_time} - {s.end_time}</p>
                  </div>
                </div>
                {renderSessionAction(s)}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="no-session-today">
          <CardContent className="p-5 text-center text-[#4A6B68]">
            <p className="text-sm">No session scheduled today. Take a moment to breathe.</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="quick-actions">
        <Link to="/sessions">
          <Card className="bg-white border-[#E5F2F1] rounded-2xl hover:shadow-[0_8px_30px_rgba(13,139,125,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#0D8B7D]/10 flex items-center justify-center"><Calendar className="w-5 h-5 text-[#0D8B7D]" /></div>
              <div><p className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">{stats.completed || 0}</p><p className="text-xs text-[#4A6B68]">Sessions Completed</p></div>
            </CardContent>
          </Card>
        </Link>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#0A6F64]/10 flex items-center justify-center"><User className="w-5 h-5 text-[#0A6F64]" /></div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#1A3330]">{therapist ? formatName(therapist) : 'Not assigned'}</p>
              <p className="text-xs text-[#4A6B68]">{therapist?.specializations?.join(', ') || 'My Therapist'}</p>
              {(therapist?.ncahp_id || therapist?.apa_id) && (
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="therapist-registrations">
                  {therapist.ncahp_id && <span className="text-[10px] text-[#0A6F64] bg-[#0A6F64]/10 rounded-full px-2 py-0.5">NCAHP: {therapist.ncahp_id}</span>}
                  {therapist.apa_id && <span className="text-[10px] text-[#0A6F64] bg-[#0A6F64]/10 rounded-full px-2 py-0.5">APA: {therapist.apa_id}</span>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Session Tabs */}
      <Tabs defaultValue="upcoming" className="w-full" data-testid="session-tabs">
        <TabsList className="bg-[#F4FAF9] rounded-xl p-1 w-fit">
          <TabsTrigger value="past" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68] px-4">Past</TabsTrigger>
          <TabsTrigger value="today" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68] px-4">Today</TabsTrigger>
          <TabsTrigger value="upcoming" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68] px-4">Upcoming</TabsTrigger>
        </TabsList>
        <TabsContent value="past" className="mt-4 space-y-3">
          {pastSessions.length === 0 ? <p className="text-sm text-[#4A6B68]">No past sessions yet.</p> : pastSessions.map(s => (
            <SessionCard key={s._id} session={s} />
          ))}
        </TabsContent>
        <TabsContent value="today" className="mt-4 space-y-3">
          {todaySessions.length === 0 ? <p className="text-sm text-[#4A6B68]">No session today.</p> : todaySessions.map(s => (
            <SessionCard key={s._id} session={s} action={renderSessionAction(s)} />
          ))}
        </TabsContent>
        <TabsContent value="upcoming" className="mt-4 space-y-3">
          {upcomingSessions.length === 0 ? <p className="text-sm text-[#4A6B68]">No upcoming sessions.</p> : upcomingSessions.map(s => (
            <SessionCard key={s._id} session={s} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SessionCard({ session, action }) {
  const statusColor = { SCHEDULED: 'bg-[#0D8B7D]/10 text-[#0D8B7D]', CHECKED_IN: 'bg-[#2E8B57]/10 text-[#2E8B57]', COMPLETED: 'bg-[#4A6B68]/10 text-[#4A6B68]', CANCELLED: 'bg-[#D3544E]/10 text-[#D3544E]', NO_SHOW: 'bg-[#F5B041]/10 text-[#F5B041]' };
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`session-card-${session._id}`}>
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F4FAF9] flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#0D8B7D]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1A3330]">{session.title}</p>
            <p className="text-xs text-[#4A6B68]">{session.date} · {session.start_time} - {session.end_time} · {session.therapist_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {action}
          <Badge className={`${statusColor[session.status] || 'bg-gray-100 text-gray-600'} text-[10px] border-0`}>{session.status}</Badge>
        </div>
      </CardContent>
    </Card>
  );
}

/* Therapist/Admin Dashboard - Full Calendar */
function TherapistDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [clientCount, setClientCount] = useState(0);
  const [therapists, setTherapists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showNoShowModal, setShowNoShowModal] = useState(null);
  const [filters, setFilters] = useState({ therapist_id: '', branch_id: '', session_type: '', status: '' });
  const [newForm, setNewForm] = useState({ title: '', client_id: '', therapist_id: user?._id || '', description: '', session_type: 'IN_PERSON', date: '', start_time: '10:00', end_time: '11:00', meeting_link: '', branch_id: '', duration: 60, session_for: 'BOTH' });
  // Recurring
  const [repeat, setRepeat] = useState(false);
  const [repeatFreq, setRepeatFreq] = useState('weekly');
  const [repeatDays, setRepeatDays] = useState([]);
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [repeatUnit, setRepeatUnit] = useState('weeks');
  const [endMode, setEndMode] = useState('after');
  const [endAfter, setEndAfter] = useState(10);
  const [endUntil, setEndUntil] = useState('');
  const [excludeDates, setExcludeDates] = useState([]);
  const [excludeInput, setExcludeInput] = useState('');

  // Scroll containers for Day & Week view — auto-scroll to 8 AM on view/date change
  const dayScrollRef = useRef(null);
  const weekScrollRef = useRef(null);

  const isAdmin = user?.role === 'ADMIN';
  const TERMINAL = ['COMPLETED', 'NO_SHOW', 'CANCELLED'];

  // 4 AM (4) to 11 PM (23), inclusive
  const HOURS = Array.from({ length: 20 }, (_, i) => i + 4);
  const SCROLL_TO_HOUR = 8; // auto-scroll Day/Week view to 8 AM on mount/view-change

  // Each hour row is 56px tall (h-14) — scroll to (SCROLL_TO_HOUR - first hour) * 56
  useEffect(() => {
    const targetPx = (SCROLL_TO_HOUR - HOURS[0]) * 56;
    const t = setTimeout(() => {
      if (viewMode === 'week' && weekScrollRef.current) {
        weekScrollRef.current.scrollTop = targetPx;
      } else if (viewMode === 'day' && dayScrollRef.current) {
        dayScrollRef.current.scrollTop = targetPx;
      }
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, currentDate]);

  const STATUS_BG = { SCHEDULED: 'bg-blue-500', CHECKED_IN: 'bg-teal-500', COMPLETED: 'bg-emerald-500', CANCELLED: 'bg-red-500', NO_SHOW: 'bg-amber-500' };
  const STATUS_BORDER = { SCHEDULED: 'border-blue-600', CHECKED_IN: 'border-teal-600', COMPLETED: 'border-emerald-600', CANCELLED: 'border-red-600', NO_SHOW: 'border-amber-600' };
  const STATUS_LABEL = { SCHEDULED: 'Scheduled', CHECKED_IN: 'Checked In', COMPLETED: 'Completed', CANCELLED: 'Cancelled', NO_SHOW: 'No Show' };
  const DURATIONS = [{ l: '30m', v: 30 }, { l: '45m', v: 45 }, { l: '1h', v: 60 }, { l: '1.5h', v: 90 }, { l: '2h', v: 120 }, { l: '2.5h', v: 150 }, { l: '3h', v: 180 }];

  // Date helpers — week is Monday-first throughout.
  const fmt = (d) => d.toISOString().split('T')[0];
  // Monday-based day-of-week index: Mon=0..Sun=6
  const monDow = (d) => (d.getDay() + 6) % 7;
  const getWeekStart = (d) => { const dt = new Date(d); dt.setDate(dt.getDate() - monDow(dt)); return dt; };
  const getWeekDays = (d) => { const start = getWeekStart(d); return Array.from({ length: 7 }, (_, i) => { const day = new Date(start); day.setDate(start.getDate() + i); return day; }); };
  const getMonthDays = (d) => {
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const startDay = monDow(first); // Monday-based leading padding
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(d.getFullYear(), d.getMonth(), i));
    return days;
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() + dir * 7);
    else if (viewMode === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const getDateRange = () => {
    if (viewMode === 'week') {
      const days = getWeekDays(currentDate);
      return { from: fmt(days[0]), to: fmt(days[6]) };
    } else if (viewMode === 'month') {
      const y = currentDate.getFullYear(), m = currentDate.getMonth();
      return { from: `${y}-${String(m + 1).padStart(2, '0')}-01`, to: `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}` };
    } else {
      const d = fmt(currentDate);
      return { from: d, to: d };
    }
  };

  const fetchData = async () => {
    try {
      const range = getDateRange();
      const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
      if (filters.therapist_id) params.set('therapist_id', filters.therapist_id);
      if (filters.branch_id) params.set('branch_id', filters.branch_id);
      if (filters.session_type) params.set('session_type', filters.session_type);
      if (filters.status) params.set('status', filters.status);
      const [sRes, stRes, cRes] = await Promise.all([
        api.get(`/sessions?${params}`),
        api.get('/sessions/stats/summary'),
        api.get('/clients'),
      ]);
      setSessions(sRes.data);
      setStats(stRes.data);
      setClientCount(cRes.data.length);
      setClients(cRes.data);
    } catch {}
  };

  useEffect(() => { fetchData(); }, [currentDate, viewMode, filters]);
  useEffect(() => {
    Promise.all([api.get('/therapists'), api.get('/branches')]).then(([t, b]) => { setTherapists(t.data); setBranches(b.data); }).catch(() => {});
  }, []);

  const sessionsForDate = (dateStr) => sessions.filter(s => s.date === dateStr);

  const timeToMinutes = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const getSessionStyle = (s) => {
    const startMin = timeToMinutes(s.start_time) - 480; // offset from 8:00
    const endMin = s.end_time ? timeToMinutes(s.end_time) - 480 : startMin + 60;
    const top = (startMin / 30) * 28; // 28px per 30-min slot
    const height = Math.max(((endMin - startMin) / 30) * 28, 22);
    return { top: `${top}px`, height: `${height}px` };
  };

  const calcEnd = (st, dur) => { const [h, m] = st.split(':').map(Number); const em = h * 60 + m + dur; return `${String(Math.floor(em / 60)).padStart(2, '0')}:${String(em % 60).padStart(2, '0')}`; };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newForm, therapist_id: newForm.therapist_id || user._id };
      if (repeat) {
        payload.repeat = true;
        payload.repeat_frequency = repeatFreq;
        payload.repeat_interval = repeatInterval;
        if (['weekly', 'bi_weekly'].includes(repeatFreq)) payload.repeat_days = repeatDays;
        if (repeatFreq === 'custom') { payload.repeat_unit = repeatUnit; if (repeatUnit === 'weeks') payload.repeat_days = repeatDays; }
        if (endMode === 'after') payload.end_after_sessions = endAfter;
        else if (endMode === 'until') payload.end_until_date = endUntil;
        else payload.end_after_sessions = 52;
        if (excludeDates.length) payload.exclude_dates = excludeDates;
      }
      const res = await api.post('/sessions', payload);
      setShowNewSession(false);
      setNewForm({ title: '', client_id: '', therapist_id: user?._id || '', description: '', session_type: 'IN_PERSON', date: '', start_time: '10:00', end_time: '11:00', meeting_link: '', branch_id: '', duration: 60, session_for: 'BOTH' });
      setRepeat(false); setRepeatDays([]); setExcludeDates([]);
      fetchData();
      const count = res.data?.count;
      if (count > 1) alert(`${count} sessions created!`);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/sessions/${id}`, { status });
    setSelectedSession(null);
    fetchData();
  };

  const handleCheckIn = async (id) => { await api.put(`/sessions/${id}/check-in`); setSelectedSession(null); fetchData(); };
  const handleNoShow = async () => { if (!showNoShowModal) return; await api.put(`/sessions/${showNoShowModal._id}/no-show`); setShowNoShowModal(null); setSelectedSession(null); fetchData(); };
  const handleCancelSession = async () => { if (!showCancelModal || !cancelReason.trim()) return; await api.put(`/sessions/${showCancelModal._id}/cancel`, { reason: cancelReason.trim() }); setShowCancelModal(null); setCancelReason(''); setSelectedSession(null); fetchData(); };
  const handleDeleteSession = async () => { if (!showDeleteModal) return; try { await api.delete(`/sessions/${showDeleteModal._id}`); setShowDeleteModal(null); setDeleteReason(''); setSelectedSession(null); fetchData(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } };

  const openEditSession = (s) => {
    setEditForm({ title: s.title, description: s.description || '', session_type: s.session_type, date: s.date, start_time: s.start_time, end_time: s.end_time, meeting_link: s.meeting_link || '', branch_id: s.branch_id || '', duration: s.duration || 60 });
    setEditSession(s);
    setSelectedSession(null);
  };
  const handleEditSession = async (e) => {
    e.preventDefault();
    if (!editSession) return;
    try { await api.put(`/sessions/${editSession._id}`, editForm); setEditSession(null); fetchData(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const canEdit = (s) => { if (TERMINAL.includes(s.status)) return false; if (isAdmin) return true; if (user?.role === 'THERAPIST' && s.therapist_id === user._id && ['SCHEDULED', 'CHECKED_IN'].includes(s.status)) return true; return false; };
  const canDelete = (s) => { if (TERMINAL.includes(s.status)) return false; if (isAdmin) return true; if (user?.role === 'THERAPIST' && s.therapist_id === user._id && s.status === 'SCHEDULED') return true; return false; };
  const toggleDay = (d) => setRepeatDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const today = fmt(new Date());
  const weekDays = getWeekDays(currentDate);
  const monthDays = getMonthDays(currentDate);
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const headerLabel = viewMode === 'week'
    ? `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : viewMode === 'month'
    ? currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-4" data-testid="therapist-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-kpis">
        {[
          { label: "Today's Sessions", value: stats.today || 0, icon: Clock, color: '#3B82F6' },
          { label: 'Upcoming', value: stats.upcoming || 0, icon: Calendar, color: '#0D8B7D' },
          { label: 'Completed', value: stats.completed || 0, icon: Calendar, color: '#10B981' },
          { label: 'Total Patients', value: clientCount, icon: User, color: '#F5B041' },
        ].map((kpi, i) => (
          <Card key={i} className="bg-white border-[#E5F2F1] rounded-2xl shadow-[0_4px_20px_rgba(13,139,125,0.04)]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${kpi.color}15` }}>
                <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
              </div>
              <div><p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{kpi.value}</p><p className="text-[11px] text-[#4A6B68]">{kpi.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar: Filters + View Toggle + Navigation + New Session */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="calendar-toolbar">
        <CardContent className="p-3 space-y-3">
          {/* Row 1: Navigation + View Toggle + New Session */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 px-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())} className="h-8 text-xs text-[#0D8B7D] font-medium" data-testid="cal-today">Today</Button>
              <Button variant="ghost" size="sm" onClick={() => navigate(1)} className="h-8 px-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
              <h3 className="text-sm font-semibold text-[#1A3330] font-['Outfit'] ml-2">{headerLabel}</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-[#F4FAF9] rounded-lg p-0.5">
                {['day', 'week', 'month'].map(v => (
                  <button key={v} onClick={() => setViewMode(v)} data-testid={`view-${v}`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${viewMode === v ? 'bg-white text-[#0D8B7D] shadow-sm' : 'text-[#4A6B68] hover:text-[#1A3330]'}`}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowNewSession(true)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-xs h-8" data-testid="new-session-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Session
              </Button>
            </div>
          </div>
          {/* Row 2: Filters */}
          <div className="flex flex-wrap gap-2">
            {user?.role === 'ADMIN' && (
              <select value={filters.therapist_id} onChange={e => setFilters({ ...filters, therapist_id: e.target.value })}
                className="h-8 text-xs rounded-lg border border-[#E5F2F1] bg-white px-2 text-[#1A3330] focus:ring-1 focus:ring-[#0D8B7D]/30 focus:border-[#0D8B7D]" data-testid="filter-therapist">
                <option value="">All Therapists</option>
                {therapists.map(t => <option key={t._id} value={t._id}>{formatName(t)}</option>)}
              </select>
            )}
            {user?.role === 'THERAPIST' && (
              <select value={user._id} disabled
                className="h-8 text-xs rounded-lg border border-[#E5F2F1] bg-[#F4FAF9] px-2 text-[#1A3330] cursor-not-allowed opacity-90" data-testid="filter-therapist-locked"
                title="Therapists can only view their own sessions">
                <option value={user._id}>{formatName(user)} (You)</option>
              </select>
            )}
            <select value={filters.branch_id} onChange={e => setFilters({ ...filters, branch_id: e.target.value })}
              className="h-8 text-xs rounded-lg border border-[#E5F2F1] bg-white px-2 text-[#1A3330] focus:ring-1 focus:ring-[#0D8B7D]/30 focus:border-[#0D8B7D]" data-testid="filter-branch">
              <option value="">All Branches</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
            <select value={filters.session_type} onChange={e => setFilters({ ...filters, session_type: e.target.value })}
              className="h-8 text-xs rounded-lg border border-[#E5F2F1] bg-white px-2 text-[#1A3330] focus:ring-1 focus:ring-[#0D8B7D]/30 focus:border-[#0D8B7D]" data-testid="filter-type">
              <option value="">All Types</option>
              <option value="IN_PERSON">In-Person</option>
              <option value="VIDEO">Video</option>
            </select>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
              className="h-8 text-xs rounded-lg border border-[#E5F2F1] bg-white px-2 text-[#1A3330] focus:ring-1 focus:ring-[#0D8B7D]/30 focus:border-[#0D8B7D]" data-testid="filter-status">
              <option value="">All Statuses</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="NO_SHOW">No-Show</option>
            </select>
            {(filters.therapist_id || filters.branch_id || filters.session_type || filters.status) && (
              <button onClick={() => setFilters({ therapist_id: '', branch_id: '', session_type: '', status: '' })} className="h-8 text-xs text-[#D3544E] hover:underline px-1" data-testid="clear-filters">Clear filters</button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden" data-testid="full-calendar">
        <CardContent className="p-0">

          {/* ===== WEEK VIEW ===== */}
          {viewMode === 'week' && (
            <div className="overflow-auto max-h-[calc(100vh-280px)]" ref={weekScrollRef} data-testid="week-view-scroll">
              {/* Header row */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[#E5F2F1] sticky top-0 bg-white z-10">
                <div className="p-2 border-r border-[#E5F2F1]" />
                {weekDays.map((d, i) => {
                  const isToday = fmt(d) === today;
                  return (
                    <div key={i} className={`p-2 text-center border-r border-[#E5F2F1] last:border-r-0 ${isToday ? 'bg-blue-50' : ''}`}>
                      <p className="text-[10px] font-medium text-[#4A6B68] uppercase">{dayNames[i]}</p>
                      <p className={`text-lg font-semibold font-['Outfit'] ${isToday ? 'text-blue-600' : 'text-[#1A3330]'}`}>{d.getDate()}</p>
                    </div>
                  );
                })}
              </div>
              {/* Time grid */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)]">
                {/* Time labels column + 7 day columns rendered row by row */}
                {HOURS.map(hour => (
                  <div key={hour} className="contents">
                    {/* Time label */}
                    <div className="h-14 border-r border-b border-[#E5F2F1] flex items-start justify-end pr-2 pt-0.5">
                      <span className="text-[10px] text-[#4A6B68] font-medium">{hour > 12 ? hour - 12 : hour}{hour >= 12 ? 'PM' : 'AM'}</span>
                    </div>
                    {/* Day cells */}
                    {weekDays.map((d, di) => {
                      const dateStr = fmt(d);
                      const isToday = dateStr === today;
                      const cellSessions = sessionsForDate(dateStr).filter(s => {
                        const sHour = parseInt(s.start_time?.split(':')[0]);
                        return sHour === hour;
                      });
                      return (
                        <div key={di} className={`h-14 border-r border-b border-[#E5F2F1] last:border-r-0 relative ${isToday ? 'bg-blue-50/30' : ''}`}>
                          {cellSessions.map(s => {
                            const startMin = timeToMinutes(s.start_time);
                            const endMin = s.end_time ? timeToMinutes(s.end_time) : startMin + 60;
                            const duration = endMin - startMin;
                            const offsetMin = startMin - hour * 60;
                            const topPx = (offsetMin / 60) * 56;
                            const heightPx = Math.max((duration / 60) * 56, 20);
                            const isCouple = !!s.partner_client_name;
                            const displayName = isCouple ? `🔗 ${s.client_name?.split(' ')[0]} & ${s.partner_client_name?.split(' ')[0]}` : s.client_name;
                            return (
                              <button key={s._id} onClick={() => setSelectedSession(s)} data-testid={`session-block-${s._id}`}
                                title={`${s.title}\n${isCouple ? `Couple: ${s.client_name} & ${s.partner_client_name}` : (s.client_name || '')}${s.therapist_name ? ' | ' + s.therapist_name : ''}\n${s.start_time}–${s.end_time} | ${s.session_type?.replace('_', ' ')}`}
                                className={`absolute left-0.5 right-0.5 ${STATUS_BG[s.status]} border-l-[3px] ${STATUS_BORDER[s.status]} text-white rounded-md px-1.5 py-0.5 text-left overflow-hidden cursor-pointer hover:brightness-110 transition-all z-[5]`}
                                style={{ top: `${topPx}px`, height: `${heightPx}px` }}>
                                <p className="text-[10px] font-semibold truncate leading-tight">{s.title}</p>
                                {heightPx > 28 && <p className="text-[9px] opacity-90 truncate leading-tight">{displayName}{s.therapist_name ? ` | ${s.therapist_name}` : ''}</p>}
                                {heightPx > 42 && (
                                  <p className="text-[9px] opacity-80 truncate leading-tight flex items-center gap-1">
                                    <span>{s.start_time}–{s.end_time}</span>
                                    {s.session_type === 'VIDEO' ? <Video className="w-2.5 h-2.5" /> : s.session_type === 'TELE' ? <Phone className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                                  </p>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== MONTH VIEW ===== */}
          {viewMode === 'month' && (
            <div>
              <div className="grid grid-cols-7 border-b border-[#E5F2F1]">
                {dayNames.map(d => <div key={d} className="p-2 text-center text-[10px] font-semibold text-[#4A6B68] uppercase tracking-wider border-r border-[#E5F2F1] last:border-r-0">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {monthDays.map((d, i) => {
                  if (!d) return <div key={`e-${i}`} className="min-h-[80px] border-r border-b border-[#E5F2F1]" />;
                  const dateStr = fmt(d);
                  const isToday = dateStr === today;
                  const daySessions = sessionsForDate(dateStr);
                  return (
                    <div key={i} className={`min-h-[80px] p-1 border-r border-b border-[#E5F2F1] ${isToday ? 'bg-blue-50/40' : ''}`}>
                      <span className={`text-xs font-medium ${isToday ? 'bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-[#1A3330]'}`}>{d.getDate()}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {daySessions.slice(0, 3).map(s => (
                          <button key={s._id} onClick={() => setSelectedSession(s)}
                            title={`${s.title}\n${s.partner_client_name ? `Couple: ${s.client_name} & ${s.partner_client_name}` : (s.client_name || '')}${s.therapist_name ? ' | ' + s.therapist_name : ''}\n${s.start_time}–${s.end_time} | ${s.session_type?.replace('_', ' ')}`}
                            data-testid={`month-session-${s._id}`}
                            className={`w-full text-left ${STATUS_BG[s.status]} text-white text-[9px] px-1 py-0.5 rounded truncate cursor-pointer hover:brightness-110`}>
                            {s.partner_client_name ? '🔗 ' : ''}{s.start_time} {s.client_name?.split(' ')[0]}{s.partner_client_name ? `&${s.partner_client_name?.split(' ')[0]}` : (s.therapist_name ? ` · Dr ${s.therapist_name.split(' ').slice(-1)[0]}` : '')}
                          </button>
                        ))}
                        {daySessions.length > 3 && <p className="text-[9px] text-[#4A6B68] pl-1">+{daySessions.length - 3} more</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== DAY VIEW ===== */}
          {viewMode === 'day' && (
            <div className="overflow-auto max-h-[calc(100vh-280px)]" ref={dayScrollRef} data-testid="day-view-scroll">
              <div className="p-3 border-b border-[#E5F2F1] bg-[#F4FAF9] sticky top-0 z-10">
                <p className="text-sm font-semibold text-[#1A3330] font-['Outfit']">{currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                <p className="text-xs text-[#4A6B68]">{sessionsForDate(fmt(currentDate)).length} sessions</p>
              </div>
              {HOURS.map(hour => {
                const cellSessions = sessionsForDate(fmt(currentDate)).filter(s => parseInt(s.start_time?.split(':')[0]) === hour);
                return (
                  <div key={hour} className="flex border-b border-[#E5F2F1]">
                    <div className="w-16 py-3 pr-2 text-right flex-shrink-0 border-r border-[#E5F2F1]">
                      <span className="text-xs text-[#4A6B68] font-medium">{hour > 12 ? hour - 12 : hour}:00 {hour >= 12 ? 'PM' : 'AM'}</span>
                    </div>
                    <div className="flex-1 min-h-[56px] p-1 relative">
                      {cellSessions.map(s => {
                        const startMin = timeToMinutes(s.start_time);
                        const endMin = s.end_time ? timeToMinutes(s.end_time) : startMin + 60;
                        const duration = endMin - startMin;
                        return (
                          <button key={s._id} onClick={() => setSelectedSession(s)} data-testid={`day-session-${s._id}`}
                            title={`${s.title}\n${s.partner_client_name ? `Couple: ${s.client_name} & ${s.partner_client_name}` : (s.client_name || '')}${s.therapist_name ? ' | ' + s.therapist_name : ''}\n${s.start_time}–${s.end_time} | ${s.session_type?.replace('_', ' ')}`}
                            className={`w-full mb-1 ${STATUS_BG[s.status]} border-l-[3px] ${STATUS_BORDER[s.status]} text-white rounded-lg px-3 py-2 text-left cursor-pointer hover:brightness-110 transition-all`}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold flex items-center gap-1">{s.partner_client_name && <span title="Couple session">🔗</span>}{s.title}</p>
                              <span className="text-[10px] opacity-80">{duration}min</span>
                            </div>
                            <p className="text-xs opacity-90">{s.partner_client_name ? `${s.client_name} & ${s.partner_client_name}` : s.client_name}{s.therapist_name ? ` | ${s.therapist_name}` : ''}{!s.partner_client_name && s.client_type === 'RELATIONSHIP' ? <span className="ml-1 text-[9px] opacity-75 bg-white/20 px-1 py-0.5 rounded">Relationship</span> : null}</p>
                            <p className="text-[10px] opacity-80 mt-0.5 flex items-center gap-1">
                              <span>{s.start_time}–{s.end_time}</span>
                              <span className="opacity-60">|</span>
                              {s.session_type === 'VIDEO' ? <Video className="w-3 h-3" /> : s.session_type === 'TELE' ? <Phone className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                              <span>{s.session_type.replace('_', ' ')}</span>
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No sessions */}
          {sessions.length === 0 && (
            <div className="p-10 text-center text-sm text-[#4A6B68]">No sessions found for this period{filters.therapist_id || filters.branch_id || filters.session_type || filters.status ? ' with the selected filters' : ''}.</div>
          )}
        </CardContent>
      </Card>

      {/* ===== SESSION DETAIL MODAL ===== */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSession(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()} data-testid="session-detail-modal">
            <div className={`${STATUS_BG[selectedSession.status] || 'bg-gray-500'} rounded-t-2xl px-5 py-4`}>
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold font-['Outfit'] text-lg">{selectedSession.title}</h3>
                <div className="flex items-center gap-1">
                  {canEdit(selectedSession) && (
                    <button onClick={() => openEditSession(selectedSession)} className="text-white/70 hover:text-white" data-testid="dash-edit-session-btn"><Pencil className="w-4 h-4" /></button>
                  )}
                  {canDelete(selectedSession) && (
                    <button onClick={() => { setShowDeleteModal(selectedSession); setDeleteReason(''); }} className="text-white/70 hover:text-white" data-testid="dash-delete-session-btn"><Trash2 className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => setSelectedSession(null)} className="text-white/70 hover:text-white ml-1"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <p className="text-white/80 text-sm mt-1">{STATUS_LABEL[selectedSession.status] || selectedSession.status}</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[10px] text-[#4A6B68] uppercase tracking-wider mb-0.5">Client</p><p className="text-[#1A3330] font-medium">{selectedSession.client_name || 'N/A'}</p></div>
                <div><p className="text-[10px] text-[#4A6B68] uppercase tracking-wider mb-0.5">Therapist</p><p className="text-[#1A3330] font-medium">{selectedSession.therapist_name || 'N/A'}</p></div>
                <div><p className="text-[10px] text-[#4A6B68] uppercase tracking-wider mb-0.5">Date & Time</p><p className="text-[#1A3330]">{selectedSession.date}<br/>{selectedSession.start_time} – {selectedSession.end_time}</p></div>
                <div><p className="text-[10px] text-[#4A6B68] uppercase tracking-wider mb-0.5">Type</p><p className="text-[#1A3330] flex items-center gap-1">{selectedSession.session_type === 'VIDEO' ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}{selectedSession.session_type.replace('_', ' ')}</p></div>
              </div>
              {selectedSession.session_type === 'VIDEO' && selectedSession.meeting_link && (
                <a href={selectedSession.meeting_link} target="_blank" rel="noreferrer" className="block">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm" data-testid="join-video-btn"><Video className="w-4 h-4 mr-2" />Join Video Session</Button>
                </a>
              )}
              {selectedSession.branch_name && (
                <div className="bg-[#F4FAF9] p-3 rounded-xl text-sm"><p className="text-[10px] text-[#4A6B68] uppercase tracking-wider mb-0.5">Branch</p><p className="text-[#1A3330]">{selectedSession.branch_name}</p></div>
              )}
              {selectedSession.description && <p className="text-sm text-[#4A6B68] border-t border-[#E5F2F1] pt-3">{selectedSession.description}</p>}
              {selectedSession.cancellation_reason && <p className="text-xs text-red-500 italic">Cancelled: {selectedSession.cancellation_reason}</p>}
              {/* Status action buttons */}
              {selectedSession.status === 'SCHEDULED' && (
                <div className="flex gap-2 pt-2 border-t border-[#E5F2F1]">
                  <Button size="sm" onClick={() => handleCheckIn(selectedSession._id)} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-xs" data-testid="modal-checkin-btn">Check In</Button>
                  <Button size="sm" onClick={() => { setShowNoShowModal(selectedSession); }} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs" data-testid="modal-noshow-btn">No Show</Button>
                  <Button size="sm" onClick={() => { setShowCancelModal(selectedSession); setCancelReason(''); }} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs" data-testid="modal-cancel-btn">Cancel</Button>
                </div>
              )}
              {selectedSession.status === 'CHECKED_IN' && (
                <div className="flex gap-2 pt-2 border-t border-[#E5F2F1]">
                  <Button size="sm" onClick={() => updateStatus(selectedSession._id, 'COMPLETED')} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs" data-testid="modal-complete-btn">Mark Complete</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== NEW SESSION MODAL ===== */}
      {showNewSession && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowNewSession(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-session-modal">
            <div className="px-5 py-4 border-b border-[#E5F2F1] flex items-center justify-between">
              <h3 className="font-semibold text-[#1A3330] font-['Outfit'] text-lg">Schedule New Session</h3>
              <button onClick={() => setShowNewSession(false)} className="text-[#4A6B68] hover:text-[#1A3330]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreateSession} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Title</label><input value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" data-testid="new-session-title" /></div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Client</label>
                  <SearchableSelect
                    value={newForm.client_id}
                    onChange={(id) => setNewForm({ ...newForm, client_id: id, session_for: 'BOTH' })}
                    options={clients}
                    required
                    placeholder="Search by name, phone or email…"
                    testId="new-session-client"
                    getSearchText={(c) => `${c.first_name || ''} ${c.last_name || ''} ${c.phone || ''} ${c.email || ''} ${c.linked_partner_name || ''}`}
                    getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                    renderOption={(c) => {
                      const isCouple = c.client_type === 'RELATIONSHIP' && c.linked_partner_name;
                      const typeLabel = c.client_type === 'CHILD' ? 'Child' : c.client_type === 'RELATIONSHIP' ? 'Relationship' : 'Adult';
                      const typeClass = c.client_type === 'CHILD'
                        ? 'bg-amber-100 text-amber-800'
                        : c.client_type === 'RELATIONSHIP'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-[#E8F5F3] text-[#075E55]';
                      return (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-[#1A3330] truncate">
                              {formatName(c)}
                              {isCouple && <span className="ml-1 text-xs font-normal text-[#4A6B68]">(linked with {c.linked_partner_name})</span>}
                            </p>
                            <p className="text-[11px] text-[#4A6B68] truncate">{c.phone || c.email || '—'}</p>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${typeClass}`}>{typeLabel}</span>
                        </div>
                      );
                    }}
                    renderSelected={(c) => {
                      const isCouple = c.client_type === 'RELATIONSHIP' && c.linked_partner_name;
                      return (
                        <span className="text-sm text-[#1A3330] truncate block">
                          <strong>{formatName(c)}</strong>
                          {isCouple && <span className="text-[#4A6B68] font-normal"> (linked with {c.linked_partner_name})</span>}
                        </span>
                      );
                    }}
                  />
                </div>
                {(() => {
                  const sel = clients.find(c => c._id === newForm.client_id);
                  if (!sel || sel.client_type !== 'RELATIONSHIP' || !sel.linked_partner_id) return null;
                  return (
                    <div className="col-span-2 bg-[#F4FAF9] rounded-xl p-3" data-testid="session-for-radio">
                      <p className="text-xs font-medium text-[#1A3330] mb-2">Session For</p>
                      <div className="flex flex-col gap-1.5 text-sm text-[#1A3330]">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="session_for" value="BOTH" checked={newForm.session_for === 'BOTH'} onChange={() => setNewForm({...newForm, session_for: 'BOTH'})} data-testid="session-for-both" />
                          <span>Both Partners ({formatName(sel)} &amp; {sel.linked_partner_name}) <span className="text-[#0D8B7D]">🔗</span></span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="session_for" value="PRIMARY" checked={newForm.session_for === 'PRIMARY'} onChange={() => setNewForm({...newForm, session_for: 'PRIMARY'})} data-testid="session-for-primary" />
                          <span>{formatName(sel)} only</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="session_for" value="PARTNER" checked={newForm.session_for === 'PARTNER'} onChange={() => setNewForm({...newForm, session_for: 'PARTNER', client_id: sel.linked_partner_id})} data-testid="session-for-partner" />
                          <span>{sel.linked_partner_name} only</span>
                        </label>
                      </div>
                    </div>
                  );
                })()}
                {user?.role === 'ADMIN' && (
                  <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Therapist</label>
                    <SearchableSelect
                      value={newForm.therapist_id}
                      onChange={(id) => setNewForm({ ...newForm, therapist_id: id })}
                      options={therapists}
                      placeholder="Search therapist by name…"
                      testId="new-session-therapist"
                      getSearchText={(t) => `${t.first_name || ''} ${t.last_name || ''} ${t.email || ''} ${t.specialization || ''}`}
                      getLabel={(t) => {
                        const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                        return `Dr. ${fn} ${t.last_name || ''}`.trim();
                      }}
                      renderOption={(t) => {
                        const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                        return (
                          <div>
                            <p className="text-sm font-semibold text-[#1A3330] truncate">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</p>
                            <p className="text-[11px] text-[#4A6B68] truncate">{t.specialization || t.email || '—'}</p>
                          </div>
                        );
                      }}
                      renderSelected={(t) => {
                        const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                        return <span className="text-sm font-medium text-[#1A3330] truncate block">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</span>;
                      }}
                    />
                  </div>
                )}
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Type</label>
                  <select value={newForm.session_type} onChange={e => setNewForm({ ...newForm, session_type: e.target.value })} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" data-testid="new-session-type">
                    <option value="IN_PERSON">In-Person</option><option value="VIDEO">Video</option>
                  </select>
                </div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Duration</label>
                  <select value={newForm.duration} onChange={e => { const d = parseInt(e.target.value); setNewForm(f => ({ ...f, duration: d, end_time: calcEnd(f.start_time, d) })); }} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                    {DURATIONS.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Date</label><input type="date" value={newForm.date} onChange={e => setNewForm({ ...newForm, date: e.target.value })} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" data-testid="new-session-date" /></div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Start Time</label><input type="time" value={newForm.start_time} onChange={e => { const st = e.target.value; setNewForm(f => ({ ...f, start_time: st, end_time: calcEnd(st, f.duration) })); }} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Branch</label>
                  <select value={newForm.branch_id} onChange={e => setNewForm({ ...newForm, branch_id: e.target.value })} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                    <option value="">Select branch...</option>{branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Meeting Link {newForm.session_type === 'VIDEO' && <span className="text-red-500">*</span>}</label><input value={newForm.meeting_link} onChange={e => setNewForm({ ...newForm, meeting_link: e.target.value })} required={newForm.session_type === 'VIDEO'} placeholder="https://meet.google.com/..." className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Description</label><textarea value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} rows={2} className="w-full rounded-xl border border-[#E5F2F1] px-3 py-2 text-sm" /></div>
                {/* Recurring */}
                <div className="col-span-2 border-t border-[#E5F2F1] pt-3">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={repeat} onChange={e => setRepeat(e.target.checked)} className="rounded" data-testid="dash-repeat-toggle" /><Repeat className="w-4 h-4 text-[#0D8B7D]" /><span className="text-sm font-medium text-[#1A3330]">Repeat</span></label>
                </div>
                {repeat && (
                  <div className="col-span-2 space-y-3 bg-[#F4FAF9] p-3 rounded-xl" data-testid="dash-repeat-options">
                    <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Frequency</label>
                      <select value={repeatFreq} onChange={e => setRepeatFreq(e.target.value)} className="w-full h-8 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                        <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="bi_weekly">Bi-Weekly</option><option value="monthly">Monthly</option><option value="custom">Custom</option>
                      </select>
                    </div>
                    {['weekly', 'bi_weekly'].includes(repeatFreq) && (
                      <div><label className="text-xs mb-1 block">Days</label><div className="flex gap-1">{DAY_NAMES_SHORT.map((d, i) => (<button key={d} type="button" onClick={() => toggleDay(i)} className={`w-8 h-8 rounded-lg text-[10px] border ${repeatDays.includes(i) ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'border-[#E5F2F1] text-[#1A3330]'}`}>{d}</button>))}</div></div>
                    )}
                    {repeatFreq === 'custom' && (
                      <div className="flex gap-2 items-end"><div><label className="text-xs mb-1 block">Every</label><input type="number" min={1} value={repeatInterval} onChange={e => setRepeatInterval(parseInt(e.target.value) || 1)} className="w-16 h-8 rounded-xl border border-[#E5F2F1] px-2 text-sm" /></div>
                        <select value={repeatUnit} onChange={e => setRepeatUnit(e.target.value)} className="h-8 rounded-xl border border-[#E5F2F1] px-2 text-sm"><option value="days">Days</option><option value="weeks">Weeks</option><option value="months">Months</option></select></div>
                    )}
                    {repeatFreq === 'custom' && repeatUnit === 'weeks' && (
                      <div><label className="text-xs mb-1 block">Days</label><div className="flex gap-1">{DAY_NAMES_SHORT.map((d, i) => (<button key={d} type="button" onClick={() => toggleDay(i)} className={`w-8 h-8 rounded-lg text-[10px] border ${repeatDays.includes(i) ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'border-[#E5F2F1] text-[#1A3330]'}`}>{d}</button>))}</div></div>
                    )}
                    <div><label className="text-xs mb-1 block">End</label><div className="space-y-1">
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="dashEnd" checked={endMode==='after'} onChange={() => setEndMode('after')} /> After <input type="number" min={1} value={endAfter} onChange={e => setEndAfter(parseInt(e.target.value)||1)} className="w-14 h-7 rounded-lg border border-[#E5F2F1] px-2 text-xs" disabled={endMode!=='after'} /> sessions</label>
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="dashEnd" checked={endMode==='until'} onChange={() => setEndMode('until')} /> Until <input type="date" value={endUntil} onChange={e => setEndUntil(e.target.value)} className="h-7 rounded-lg border border-[#E5F2F1] px-2 text-xs w-36" disabled={endMode!=='until'} /></label>
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="dashEnd" checked={endMode==='none'} onChange={() => setEndMode('none')} /> No end date</label>
                    </div></div>
                    <div><label className="text-xs mb-1 block">Exclude Dates</label><div className="flex gap-1"><input type="date" value={excludeInput} onChange={e => setExcludeInput(e.target.value)} className="h-7 rounded-lg border border-[#E5F2F1] px-2 text-xs flex-1" /><button type="button" onClick={() => { if (excludeInput && !excludeDates.includes(excludeInput)) { setExcludeDates([...excludeDates, excludeInput]); setExcludeInput(''); } }} className="h-7 px-2 text-xs rounded-lg border border-[#E5F2F1]">Add</button></div>
                      {excludeDates.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{excludeDates.map(d => (<span key={d} onClick={() => setExcludeDates(prev => prev.filter(x => x !== d))} className="bg-red-100 text-red-600 text-[9px] px-2 py-0.5 rounded-full cursor-pointer">{d} ×</span>))}</div>}
                    </div>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="create-session-submit">Schedule Session</Button>
            </form>
          </div>
        </div>
      )}

      {/* ===== EDIT SESSION MODAL ===== */}
      {editSession && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setEditSession(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="dash-edit-session-modal">
            <div className="px-5 py-4 border-b border-[#E5F2F1] flex items-center justify-between">
              <h3 className="font-semibold text-[#1A3330] font-['Outfit'] text-lg">Edit Session</h3>
              <button onClick={() => setEditSession(null)} className="text-[#4A6B68] hover:text-[#1A3330]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditSession} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Title</label><input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Type</label>
                  <select value={editForm.session_type} onChange={e => setEditForm({...editForm, session_type: e.target.value})} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                    <option value="IN_PERSON">In-Person</option><option value="VIDEO">Video</option>
                  </select>
                </div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Duration</label>
                  <select value={editForm.duration} onChange={e => { const d = parseInt(e.target.value); setEditForm(f => ({...f, duration: d, end_time: calcEnd(f.start_time, d)})); }} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                    {DURATIONS.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Date</label><input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div><label className="text-xs font-medium text-[#1A3330] mb-1 block">Start Time</label><input type="time" value={editForm.start_time} onChange={e => { const st = e.target.value; setEditForm(f => ({...f, start_time: st, end_time: calcEnd(st, f.duration)})); }} required className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Branch</label>
                  <select value={editForm.branch_id} onChange={e => setEditForm({...editForm, branch_id: e.target.value})} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm">
                    <option value="">Select branch...</option>{branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Meeting Link</label><input value={editForm.meeting_link} onChange={e => setEditForm({...editForm, meeting_link: e.target.value})} className="w-full h-9 rounded-xl border border-[#E5F2F1] px-3 text-sm" /></div>
                <div className="col-span-2"><label className="text-xs font-medium text-[#1A3330] mb-1 block">Description</label><textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} className="w-full rounded-xl border border-[#E5F2F1] px-3 py-2 text-sm" /></div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="dash-save-edit-btn">Save Changes</Button>
                <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setEditSession(null)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== CANCEL SESSION MODAL ===== */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowCancelModal(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="dash-cancel-modal">
            <h3 className="font-semibold text-[#1A3330] font-['Outfit']">Cancel Session</h3>
            <div className="bg-red-50 p-3 rounded-xl"><p className="text-sm font-medium text-[#1A3330]">{showCancelModal.title}</p><p className="text-xs text-[#4A6B68]">{showCancelModal.client_name} · {showCancelModal.date}</p></div>
            <div><label className="text-sm text-[#1A3330]">Reason <span className="text-red-500">*</span></label><textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} className="w-full rounded-xl border border-[#E5F2F1] px-3 py-2 text-sm mt-1" placeholder="Provide reason..." data-testid="dash-cancel-reason" /></div>
            <div className="flex gap-2"><Button onClick={handleCancelSession} disabled={!cancelReason.trim()} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl" data-testid="dash-confirm-cancel">Confirm</Button><Button variant="outline" className="rounded-xl" onClick={() => setShowCancelModal(null)}>Go Back</Button></div>
          </div>
        </div>
      )}

      {/* ===== NO SHOW MODAL ===== */}
      {showNoShowModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowNoShowModal(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="dash-noshow-modal">
            <h3 className="font-semibold text-[#1A3330] font-['Outfit']">Mark as No Show</h3>
            <p className="text-sm text-[#4A6B68]">Mark this session as No Show?</p>
            <div className="bg-amber-50 p-3 rounded-xl"><p className="text-sm font-medium text-[#1A3330]">{showNoShowModal.title}</p><p className="text-xs text-[#4A6B68]">{showNoShowModal.client_name} · {showNoShowModal.date}</p></div>
            <div className="flex gap-2"><Button onClick={handleNoShow} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl" data-testid="dash-confirm-noshow">Confirm</Button><Button variant="outline" className="rounded-xl" onClick={() => setShowNoShowModal(null)}>Cancel</Button></div>
          </div>
        </div>
      )}

      {/* ===== DELETE SESSION MODAL ===== */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="dash-delete-modal">
            <h3 className="font-semibold text-red-500 font-['Outfit']">Delete Session</h3>
            <p className="text-sm text-[#4A6B68]">Are you sure you want to permanently delete this session? This action cannot be undone.</p>
            <div className="bg-red-50 p-3 rounded-xl"><p className="text-sm font-medium text-[#1A3330]">{showDeleteModal.title}</p><p className="text-xs text-[#4A6B68]">{showDeleteModal.client_name} · {showDeleteModal.date} {showDeleteModal.start_time}-{showDeleteModal.end_time}</p></div>
            <div><label className="text-sm text-[#1A3330]">Reason <span className="text-red-500">*</span></label><textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} rows={2} className="w-full rounded-xl border border-[#E5F2F1] px-3 py-2 text-sm mt-1" placeholder="Reason for deletion..." data-testid="dash-delete-reason" /></div>
            <div className="flex gap-2"><Button onClick={handleDeleteSession} disabled={!deleteReason.trim()} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl" data-testid="dash-confirm-delete">Confirm Delete</Button><Button variant="outline" className="rounded-xl" onClick={() => setShowDeleteModal(null)}>Cancel</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.role === 'CLIENT') return <ClientDashboard />;
  return <TherapistDashboard />;
}
